import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { BugReport as BugReportIcon } from '@mui/icons-material';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';

const BugReportDialog = ({ open, onClose }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    reporter_name: currentUser?.email?.split('@')[0] || '',
    reporter_email: currentUser?.email || '',
    bug_description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.reporter_name.trim()) {
      setError('Please enter your name');
      setLoading(false);
      return;
    }
    if (!formData.reporter_email.trim()) {
      setError('Please enter your email');
      setLoading(false);
      return;
    }
    if (!formData.bug_description.trim()) {
      setError('Please describe the bug');
      setLoading(false);
      return;
    }

    try {
      // Determine user role if logged in
      let userRole = null;
      if (currentUser) {
        userRole = currentUser.role_id === 1 ? 'Vendor' : currentUser.role_id === 2 ? 'Organizer' : null;
      }

      const response = await api.post('/bug-report', {
        reporter_email: formData.reporter_email,
        reporter_name: formData.reporter_name,
        bug_description: formData.bug_description,
        user_role: userRole,
      });

      if (response.data.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          reporter_name: currentUser?.email?.split('@')[0] || '',
          reporter_email: currentUser?.email || '',
          bug_description: '',
        });
        // Close dialog after 2 seconds
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Error submitting bug report:', err);
      setError(err.response?.data?.detail || 'Failed to submit bug report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setSuccess(false);
      setFormData({
        reporter_name: currentUser?.email?.split('@')[0] || '',
        reporter_email: currentUser?.email || '',
        bug_description: '',
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon color="primary" />
          <Typography variant="h6">Report a Bug</Typography>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Bug report submitted successfully! Thank you for helping us improve Vendoroll.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="reporter_name"
            label="Your Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.reporter_name}
            onChange={handleChange}
            required
            disabled={loading || success}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="reporter_email"
            label="Your Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.reporter_email}
            onChange={handleChange}
            required
            disabled={loading || success}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="bug_description"
            label="Bug Description"
            type="text"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={formData.bug_description}
            onChange={handleChange}
            required
            disabled={loading || success}
            placeholder="Please describe the bug you encountered, including steps to reproduce if possible..."
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            Your bug report will be sent to our team for review.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || success}
            startIcon={loading ? <CircularProgress size={20} /> : <BugReportIcon />}
          >
            {loading ? 'Submitting...' : success ? 'Submitted!' : 'Submit Report'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BugReportDialog;


